import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons/faLocationDot';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MailItemSendsType, SendsHistoryEntryType } from '@ukpostbox/client-data';
import {
	Colour,
	FlexAlign,
	FlexGap,
	FlexJustify,
	ItemRow,
	ItemRowCell,
	ItemRowSize,
	Pill,
	PillSize,
	StackHorizontal,
	StackVertical,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { FormatLocaleDateTime } from '@ukpostbox/helpers';
import { enGB } from 'date-fns/locale';
import { MailItemThumbnail } from '../../../Mailboxes/Components/Thumbnails/Entries/MailItemThumbnail';
import { MailItemTopUpRequiredThumbnail } from '../../../Mailboxes/Components/Thumbnails/Entries/MailItemTopUpRequiredThumbnail';
import { MailItemNoThumbnail } from '../../../Mailboxes/Components/Thumbnails/Entries/MailItemNoThumbnail';
import { MailPartTypeEnum } from '../../../Mailboxes/Components/Thumbnails/Types/ThumbnailTypes';

function SendsHistoryItemThumbnail(props: MailItemSendsType) {
	return (
		<div>
			{props.files.map((file, index) => (
				<div key={`sendsHistoryEnvelopesThumb${file.id}`}>
					{file.thumbnails && file.thumbnails[0]?.linkToFile ? (
						<MailItemThumbnail {...file.thumbnails[0]} />
					) : index === 0 && props.files[0]?.topupRequired ? (
						<MailItemTopUpRequiredThumbnail />
					) : (
						<MailItemNoThumbnail mailType={MailPartTypeEnum['Mail-Envelopes']} />
					)}
				</div>
			))}

			{props.files.length === 0 && <MailItemNoThumbnail mailType={MailPartTypeEnum['Mail-Envelopes']} />}
		</div>
	);
}

function SendsHistoryItemContent(props: SendsHistoryEntryType) {
	const { t } = useTranslation();

	return (
		<>
			<ItemRowCell>
				<SendsHistoryItemThumbnail {...props.mailItem} />
			</ItemRowCell>

			{!props.hidePageDetails && (
				<ItemRowCell>
					<StackVertical gap={FlexGap.ExtraSmall}>
						<div>
							{props.title}
							{props.title !== props.mailItem.trackingId && (
								<div className='fade'>{props.mailItem.trackingId}</div>
							)}
						</div>

						{props.isDeleted && (
							<Pill content={t('status.deleted')} colour={Colour.Alert} size={PillSize.Small} />
						)}
					</StackVertical>
				</ItemRowCell>
			)}

			{!props.hideUserDetails && <ItemRowCell>{props.user.name}</ItemRowCell>}

			<ItemRowCell>{props.address?.formatted}</ItemRowCell>

			<ItemRowCell>{props.postageOption?.name}</ItemRowCell>

			<ItemRowCell>{FormatLocaleDateTime(props.dateCreated, enGB)}</ItemRowCell>
		</>
	);
}

function SendsHistoryItemContentSmall(props: SendsHistoryEntryType) {
	return (
		<>
			<ItemRowCell>
				<SendsHistoryItemThumbnail {...props.mailItem} />
			</ItemRowCell>
			<ItemRowCell>
				<StackVertical gap={FlexGap.Small}>
					{!props.hidePageDetails && (
						<StackHorizontal justify={FlexJustify.Between} align={FlexAlign.Center}>
							<div>
								<h5 className='m-0'>{props.title}</h5>
								<span className='fade'>{props.mailItem.trackingId}</span>
							</div>
							<small className='fade'>{FormatLocaleDateTime(props.dateCreated, enGB)}</small>
						</StackHorizontal>
					)}

					<StackVertical gap={FlexGap.ExtraSmall}>
						{!props.hideUserDetails && (
							<div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--xs-unit)' }}>
								<FontAwesomeIcon icon={faUser} />
								<div>{props.user.name}</div>
							</div>
						)}

						<div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--xs-unit)' }}>
							<FontAwesomeIcon icon={faLocationDot} />
							<div>{props.address?.formatted}</div>
						</div>

						<div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--xs-unit)' }}>
							<FontAwesomeIcon icon={faEnvelope} />
							<div>{props.postageOption?.name}</div>
						</div>
					</StackVertical>
				</StackVertical>
			</ItemRowCell>
		</>
	);
}

function SendsHistoryItem(props: SendsHistoryEntryType) {
	const { isTiny, isSmall } = useLayout();

	return (
		<ItemRow
			title={`Sends History Entry ${props.id}`}
			static={!props.linkToSend}
			muted
			size={ItemRowSize.Large}
			actionLink={
				props.linkToSend ? { target: '_parent', url: `/mail/inbox/${props.mailItem.mailUuid}` } : undefined
			}
			sidePadding
			extraClass='sends-history-row'
		>
			{!isTiny && !isSmall ? <SendsHistoryItemContent {...props} /> : <SendsHistoryItemContentSmall {...props} />}
		</ItemRow>
	);
}

export default SendsHistoryItem;
